/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import { ReceptionServiceForPractitionerSimple } from '@dreryk/egabinet-private-medical-service-frontend-api';

import {
   InteractiveTable,
   InteractiveTableSelectorItemFilter,
   InteractiveTableSelectorItemKeyExtractor,
   InteractiveTableSelectorItemRenderer,
   InteractiveTableSelectorMapper,
   InteractiveTableSelectorTableKeyExtractor,
   Text,
} from '@dreryk/react-components';

import { receptionServiceTranslationKeys } from '../../../../translations';

import { ReceptionServiceFormController, ReceptionServiceFormPart } from './receptionService.types';
import { ReceptionServiceTableBean } from './ReceptionServiceInteractiveTable.types';
import { mapReceptionServiceForPractitionerSimpleToAppointmentReceptionService } from './ReceptionServiceInteractiveTableSelector.utils';

const t = receptionServiceTranslationKeys.extensions.calendar.appointment.table.selector;

type ReceptionServiceInteractiveTableSelectorProps = Pick<
   ReceptionServiceFormController,
   'register' | 'control' | 'setValue'
> & {
   readonly receptionsServices: ReceptionServiceForPractitionerSimple[];
};

export const ReceptionServiceInteractiveTableSelector = (
   props: ReceptionServiceInteractiveTableSelectorProps
): JSX.Element => {
   const mapper = useCallback<
      InteractiveTableSelectorMapper<ReceptionServiceForPractitionerSimple, ReceptionServiceTableBean>
   >((receptionService) => {
      return {
         receptionService: mapReceptionServiceForPractitionerSimpleToAppointmentReceptionService(receptionService),
      };
   }, []);

   const itemRenderer = useCallback<InteractiveTableSelectorItemRenderer<ReceptionServiceForPractitionerSimple>>(
      (receptionService, Highlight) => {
         return <Highlight node={<Text testId={'receptionServiceName'}>{receptionService.name}</Text>} />;
      },
      []
   );

   const itemKeyExtractor = useCallback<
      InteractiveTableSelectorItemKeyExtractor<ReceptionServiceForPractitionerSimple>
   >((receptionService) => {
      return receptionService.receptionServiceId.asNumber().toString();
   }, []);

   const tableKeyExtractor = useCallback<InteractiveTableSelectorTableKeyExtractor<ReceptionServiceTableBean>>(
      (value) => {
         return value.receptionService.receptionServiceId.toString();
      },
      []
   );

   const itemFilter = useCallback<InteractiveTableSelectorItemFilter<ReceptionServiceForPractitionerSimple>>((item) => {
      return [item.name];
   }, []);

   return (
      <InteractiveTable.Selector<
         ReceptionServiceForPractitionerSimple,
         ReceptionServiceTableBean,
         ReceptionServiceFormPart
      >
         register={props.register}
         testId={'receptionServiceSelector'}
         name={'receptionService.selector'}
         label={t.label}
         placeholder={t.placeholder}
         noItemsLabel={t.noReceptionServicesAssigned}
         items={props.receptionsServices}
         itemKeyExtractor={itemKeyExtractor}
         tableKeyExtractor={tableKeyExtractor}
         itemRenderer={itemRenderer}
         itemFilter={itemFilter}
         mapper={mapper}
         addRowBehavior={'prepend'}
      />
   );
};
